/**
 * The handbook's pure half: splitting a body around its picture slots, and the
 * blocks the editor can drop in.
 *
 * React-free on purpose. The split is the one piece of this section with a
 * property worth testing - an article whose slots are lost renders as a wall of
 * text with no sign that anything is missing - and testing it must not need a
 * DOM.
 */
import type { KbCard } from "../../../lib/kb";

/** One piece of a rendered article. */
export type KbSegment =
  | { kind: "html"; html: string }
  | { kind: "shot"; key: string; caption: string };

/**
 * A `<figure>` and whatever it wraps. The attribute block skips quoted strings,
 * because a caption may say "CRM > Hoidla" and a naive scan ends the tag inside
 * the sentence.
 */
const FIGURE_RE = /<figure\b((?:"[^"]*"|[^>])*)>([\s\S]*?)<\/figure>/g;
const ATTR_RE = /([a-zA-Z-]+)\s*=\s*"([^"]*)"/g;

/**
 * An attribute value as the person who typed it meant it.
 *
 * The caption is read straight out of the markup and then rendered as a React
 * text node, which escapes nothing and decodes nothing. So a caption stored the
 * correct way - "Halduskeskus &gt; Taastepunktid", escaped because a bare `>`
 * in an attribute trips every naive tag sweep downstream - reached the screen
 * with the entity showing. Storage stays escaped; this is where it is read back.
 */
function decodeAttr(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&nbsp;/g, "\u00a0")
    // Last, so a literal "&amp;gt;" does not turn into a ">".
    .replace(/&amp;/g, "&");
}

/**
 * The article, cut into text and slots.
 *
 * Everything between two slots stays one string and is handed to the browser as
 * markup; only the slots become components. That keeps the prose a single flow
 * for the reader (headings, lists and tables never straddle a boundary) while
 * letting a slot be a real control - a button, a picker, a video.
 */
export function splitBody(html: string): KbSegment[] {
  const segments: KbSegment[] = [];
  let cursor = 0;

  for (const m of html.matchAll(FIGURE_RE)) {
    const attrs = m[1] ?? "";
    let key = "";
    let caption = "";
    for (const a of attrs.matchAll(ATTR_RE)) {
      if (a[1] === "data-kb-shot") key = a[2] ?? "";
      if (a[1] === "data-kb-caption") caption = decodeAttr(a[2] ?? "");
    }
    // A figure without a slot key is ordinary markup and stays in the prose.
    if (!key) continue;

    const at = m.index ?? 0;
    if (at > cursor) segments.push({ kind: "html", html: html.slice(cursor, at) });
    segments.push({ kind: "shot", key, caption });
    cursor = at + m[0].length;
  }

  if (cursor < html.length) segments.push({ kind: "html", html: html.slice(cursor) });
  return segments;
}

/** Where a guide lives. One place that knows, so a link is never hand-built. */
export function guideHref(slug: string): string {
  return `/guides/${slug}`;
}

/** Cards grouped by shelf, each group in reading order. */
export function groupByCategory(cards: KbCard[], categories: readonly string[]): { category: string; cards: KbCard[] }[] {
  return categories
    .map((category) => ({
      category,
      cards: cards
        .filter((c) => c.category === category)
        .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title)),
    }))
    .filter((g) => g.cards.length > 0);
}

/**
 * The blocks the editor can insert.
 *
 * They exist because the body is written as HTML and the vocabulary is small
 * and specific: nobody should have to remember that a warning is
 * `<div class="kb-warn">`, and a guide is worth less every time somebody
 * invents a class the reader does not style.
 */
export const KB_SNIPPETS: { key: string; html: string }[] = [
  { key: "section", html: '<h2 id="uus-osa">6. Uus osa</h2>\n<p>...</p>' },
  { key: "step", html: "<h3>Samm N. Pealkiri</h3>\n<p>...</p>" },
  { key: "check", html: '<ul class="kb-check">\n  <li>Esimene kontroll</li>\n  <li>Teine kontroll</li>\n</ul>' },
  { key: "note", html: '<div class="kb-note">\n  <p><strong>Märkus.</strong> ...</p>\n</div>' },
  { key: "warn", html: '<div class="kb-warn">\n  <p><strong>Hoiatus.</strong> ...</p>\n</div>' },
  {
    key: "do",
    html: '<div class="kb-do">\n  <h4>Tee nii</h4>\n  <ul>\n    <li>...</li>\n  </ul>\n  <h4>Ära tee nii</h4>\n  <ul>\n    <li>...</li>\n  </ul>\n</div>',
  },
  {
    key: "table",
    html: '<table class="kb-table">\n  <thead>\n    <tr><th>Veerg</th><th>Veerg</th></tr>\n  </thead>\n  <tbody>\n    <tr><td>...</td><td>...</td></tr>\n  </tbody>\n</table>',
  },
  {
    key: "mail",
    html: '<div class="kb-mail">\n  <h4>Kirjamall: pealkiri</h4>\n  <p><strong>Teema:</strong> ...</p>\n  <p>Tere [nimi],</p>\n  <p>...</p>\n</div>',
  },
  { key: "shot", html: '<figure data-kb-shot="uus-pilt" data-kb-caption="Mida pilt peab näitama"></figure>' },
];

/** Where a block landed, and where the caret has to go after it. */
export interface BlockInsert {
  text: string;
  caret: number;
}

/**
 * Drop one block into the body at the caret.
 *
 * Appending to the end - which is what this did first - is the behaviour that
 * makes the buttons useless the moment an article is longer than a screen: you
 * click "Table" while reading section 3 and the table appears somewhere below
 * the fold, at the bottom of section 5.
 *
 * Two rules make the result predictable. A block goes on lines of its OWN, so a
 * caret resting inside a line is moved to the end of that line first - a table
 * wedged between `<p>foo` and `bar</p>` is markup nobody meant to write. And a
 * selection is replaced whole, so "select the placeholder paragraph, press
 * Checklist" swaps one for the other.
 */
export function insertBlock(body: string, start: number, end: number, html: string): BlockInsert {
  let from = start;
  let to = end;
  if (to <= from) {
    const lineEnd = body.indexOf("\n", from);
    from = to = lineEnd === -1 ? body.length : lineEnd;
  }

  const before = body.slice(0, from).replace(/\s+$/, "");
  const after = body.slice(to).replace(/^\s+/, "");
  const lead = before ? `${before}\n\n` : "";
  const tail = after ? `\n\n${after}` : "\n";

  return { text: `${lead}${html}${tail}`, caret: lead.length + html.length };
}
